import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import { Accordion, Dimmer, Icon, Loader } from '@mairistem/composants';
import { request } from '@mairistem/tools';
import { baseUrlApi } from '@/routes';
import type { ConventionDeclaration, Pacs } from '@/types';
import { StatutPacs } from '@/constants';

import InformationsEnregistrementDeclaration from '../InformationsEnregistrementDeclaration';
import InformationsDeclarationInitiale from './InformationsDeclarationInitiale';
import InformationsConventionInitiale from './InformationsConventionInitiale';
import InformationsPartenaire, {
  etatMatrimonialLibelle,
} from './InformationsPartenaire';
import InformationsDissolution from './InformationsDissolution';

interface InformationsConventionsContainerProps {
  conventionsDeclarationsId: number;
  pacs?: Pacs;
}

const InformationsConventionsContainer = (
  props: InformationsConventionsContainerProps,
): JSX.Element => {
  const { conventionsDeclarationsId, pacs } = props;

  const [loading, setLoading] = React.useState<boolean>(true);
  const [showDiff, setShowDiff] = React.useState<boolean>(false);
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const [conventionDeclarationBase, setConventionDeclarationBase] =
    React.useState<any>(null);
  const [conventionDeclaration, setConventionDeclaration] =
    React.useState<any>(null);
  // Copie locale de pacs.complementInsee : le PUT sur conventions/{id} met à
  // jour cette entité côté back sans que le pacs parent soit rechargé.
  const [complementInsee, setComplementInsee] = React.useState<any>(
    pacs?.complementInsee,
  );

  React.useEffect(() => {
    setComplementInsee(pacs?.complementInsee);
  }, [pacs?.complementInsee]);

  // Les informations INSEE sont obligatoires sauf sur un projet de PACS
  const isProjet = pacs?.derniereConvention?.statut === StatutPacs.PROJET;

  const handleToggle = React.useCallback((index: number) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  }, []);

  const fetchConvention = React.useCallback(() => {
    setLoading(true);
    return request
      .get(`${baseUrlApi}/conventions/${conventionsDeclarationsId}`)
      .then((response) => {
        setConventionDeclaration(response);
        setConventionDeclarationBase(response);
        setShowDiff(response?.action === 'Modification');
      })
      .catch((error) => {
        toast('Erreur lors de la récupération de la convention :', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [conventionsDeclarationsId]);

  React.useEffect(() => {
    fetchConvention();
  }, [fetchConvention]);

  const handleChangeConventionDeclaration = React.useCallback(
    (data: Partial<ConventionDeclaration>) => {
      setConventionDeclaration((prev: any) => ({
        ...prev,
        ...data,
        ...(data.adressePacs && {
          adressePacs: { ...prev?.adressePacs, ...data.adressePacs },
        }),
      }));
    },
    [],
  );

  const handleChangePartenaire1 = React.useCallback(
    (data: Record<string, unknown>) => {
      setConventionDeclaration((prev: any) => ({
        ...prev,
        partenaire1: { ...prev?.partenaire1, ...data },
      }));
    },
    [],
  );

  const handleChangePartenaire2 = React.useCallback(
    (data: Record<string, unknown>) => {
      setConventionDeclaration((prev: any) => ({
        ...prev,
        partenaire2: { ...prev?.partenaire2, ...data },
      }));
    },
    [],
  );

  const handleSaveConventionDeclaration = React.useCallback(async () => {
    return request
      .put(
        `${baseUrlApi}/conventions/${conventionDeclaration.identifiant}`,
        conventionDeclaration,
      )
      .then((data) => {
        const merged = { ...conventionDeclaration, ...data };
        setConventionDeclaration(merged);
        setConventionDeclarationBase(merged);
      });
  }, [conventionDeclaration]);

  const handleRollbackConventionDeclaration = React.useCallback(() => {
    setConventionDeclaration(conventionDeclarationBase);
  }, [conventionDeclarationBase]);

  const conventionDeclarationUpdated = React.useMemo(
    () => !_.isEqual(conventionDeclaration, conventionDeclarationBase),
    [conventionDeclaration, conventionDeclarationBase],
  );

  const informationsEnregistrementDeclaration = React.useMemo(() => {
    let title = '';
    switch (conventionDeclaration?.action) {
      case 'Conclusion':
        title = "INFORMATIONS D'ENREGISTREMENT DE LA DÉCLARATION INITIALE";
        break;
      case 'Modification':
        title = "INFORMATIONS D'ENREGISTREMENT DE LA DÉCLARATION MODIFICATIVE";
        break;
      case 'Dissolution':
        title = "INFORMATIONS D'ENREGISTREMENT DE LA DÉCLARATION";
        break;
      default:
        title = "INFORMATIONS D'ENREGISTREMENT DE LA DÉCLARATION";
    }

    return (
      <Accordion fluid styled secondary className="fiche-panel sous-panel">
        <Accordion.Title
          active={activeIndex === 0}
          index={0}
          onClick={() => handleToggle(0)}
        >
          <Icon name={activeIndex === 0 ? 'caret down' : 'caret right'} />
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <span>{title}</span>
            {showDiff &&
              conventionDeclaration?.changes?.infoEnregistrement >= 1 && (
                <span>
                  ({conventionDeclaration?.changes?.infoEnregistrement}{' '}
                  modification
                  {conventionDeclaration?.changes?.infoEnregistrement > 1
                    ? 's'
                    : ''}
                  )
                </span>
              )}
          </div>
        </Accordion.Title>
        <Accordion.Content active={activeIndex === 0}>
          <InformationsEnregistrementDeclaration
            conventionDeclaration={conventionDeclaration}
            showDiff={showDiff}
            updated={conventionDeclarationUpdated}
            onChange={handleChangeConventionDeclaration}
            onSave={handleSaveConventionDeclaration}
            onRollback={handleRollbackConventionDeclaration}
          />
        </Accordion.Content>
      </Accordion>
    );
  }, [
    activeIndex,
    conventionDeclaration,
    showDiff,
    conventionDeclarationUpdated,
    handleChangeConventionDeclaration,
    handleRollbackConventionDeclaration,
    handleSaveConventionDeclaration,
    handleToggle,
  ]);

  const informationsPartenaire1 = React.useMemo(
    () => (
      <Accordion fluid styled secondary className="fiche-panel sous-panel">
        <Accordion.Title
          active={activeIndex === 1}
          index={1}
          onClick={() => handleToggle(1)}
        >
          <Icon name={activeIndex === 1 ? 'caret down' : 'caret right'} />
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <span>
              {`INFORMATIONS DU PARTENAIRE 1 - ${conventionDeclaration?.partenaire1?.nomFamille} ${conventionDeclaration?.partenaire1?.prenom}`}
            </span>
            {showDiff &&
              conventionDeclaration?.changes?.infoPartenaire1 >= 1 && (
                <span>
                  ({conventionDeclaration?.changes?.infoPartenaire1}{' '}
                  modification
                  {conventionDeclaration?.changes?.infoPartenaire1 > 1
                    ? 's'
                    : ''}
                  )
                </span>
              )}
          </div>
        </Accordion.Title>
        <Accordion.Content active={activeIndex === 1}>
          <InformationsPartenaire
            partenaire={conventionDeclaration?.partenaire1}
            previousPartenaire={
              conventionDeclaration?.previousConvention?.partenaire1
            }
            perePartenaire={conventionDeclaration?.perePartenaire1}
            previousPerePartenaire={
              conventionDeclaration?.previousConvention?.perePartenaire1
            }
            merePartenaire={conventionDeclaration?.merePartenaire1}
            previousMerePartenaire={
              conventionDeclaration?.previousConvention?.merePartenaire1
            }
            EtatMatrimonial={
              complementInsee?.p1EtatMatrimonialLibelle ||
              etatMatrimonialLibelle(complementInsee?.p1EtatMatrimonialCode)
            }
            etatMatrimonialCode={complementInsee?.p1EtatMatrimonialCode}
            dateDivorce={complementInsee?.p1DateDivorce}
            dateVeuvage={complementInsee?.p1DateVeuvage}
            dejaPacse={complementInsee?.p1DejaPacse}
            nombreEnfants={complementInsee?.nombreEnfants}
            showAllInsee
            inseeObligatoire={!isProjet}
            dataInseeOld={
              // Tant que la déclaration n'a jamais été transférée à l'INSEE
              // (dateInitialeInsee null), les données saisies ne
              // sont pas "épurées" : elles doivent s'afficher normalement.
              !pacs?.dateInitialeInsee ||
              dayjs().diff(dayjs(pacs.dateInitialeInsee), 'month') <= 6
            }
            dateInitialeInsee={pacs?.dateInitialeInsee}
            showDiff={showDiff}
            numeroPartenaire={1}
            idConvention={conventionsDeclarationsId}
            partenaireKey="partenaire1"
            conventionDeclaration={conventionDeclaration}
            complementInsee={complementInsee}
            identifiantPacs={pacs?.identifiant}
            onSaved={fetchConvention}
            onComplementInseeSaved={setComplementInsee}
            updated={conventionDeclarationUpdated}
            onChange={handleChangePartenaire1}
            onSave={handleSaveConventionDeclaration}
            onRollback={handleRollbackConventionDeclaration}
          />
        </Accordion.Content>
      </Accordion>
    ),
    [
      conventionDeclaration,
      conventionDeclaration?.merePartenaire1,
      conventionDeclaration?.partenaire1,
      conventionDeclaration?.perePartenaire1,
      conventionDeclaration?.previousConvention?.partenaire1,
      conventionDeclaration?.previousConvention?.perePartenaire1,
      conventionDeclaration?.previousConvention?.merePartenaire1,
      complementInsee,
      complementInsee?.nombreEnfants,
      complementInsee?.p1DateDivorce,
      complementInsee?.p1DateVeuvage,
      complementInsee?.p1DejaPacse,
      complementInsee?.p1EtatMatrimonialCode,
      complementInsee?.p1EtatMatrimonialLibelle,
      pacs?.dateInitialeInsee,
      pacs?.identifiant,
      isProjet,
      activeIndex,
      handleToggle,
      showDiff,
      conventionDeclaration?.changes?.infoPartenaire1,
      conventionsDeclarationsId,
      fetchConvention,
      conventionDeclarationUpdated,
      handleChangePartenaire1,
      handleSaveConventionDeclaration,
      handleRollbackConventionDeclaration,
    ],
  );

  const informationsPartenaire2 = React.useMemo(
    () => (
      <Accordion fluid styled secondary className="fiche-panel sous-panel">
        <Accordion.Title
          active={activeIndex === 2}
          index={2}
          onClick={() => handleToggle(2)}
        >
          <Icon name={activeIndex === 2 ? 'caret down' : 'caret right'} />
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <span>
              {`INFORMATIONS DU PARTENAIRE 2 - ${conventionDeclaration?.partenaire2?.nomFamille} ${conventionDeclaration?.partenaire2?.prenom}`}
            </span>
            {showDiff &&
              conventionDeclaration?.changes?.infoPartenaire2 >= 1 && (
                <span>
                  ({conventionDeclaration?.changes?.infoPartenaire2}{' '}
                  modification
                  {conventionDeclaration?.changes?.infoPartenaire2 > 1
                    ? 's'
                    : ''}
                  )
                </span>
              )}
          </div>
        </Accordion.Title>
        <Accordion.Content active={activeIndex === 2}>
          <InformationsPartenaire
            partenaire={conventionDeclaration?.partenaire2}
            previousPartenaire={
              conventionDeclaration?.previousConvention?.partenaire2
            }
            perePartenaire={conventionDeclaration?.perePartenaire2}
            previousPerePartenaire={
              conventionDeclaration?.previousConvention?.perePartenaire2
            }
            merePartenaire={conventionDeclaration?.merePartenaire2}
            previousMerePartenaire={
              conventionDeclaration?.previousConvention?.merePartenaire2
            }
            EtatMatrimonial={
              complementInsee?.p2EtatMatrimonialLibelle ||
              etatMatrimonialLibelle(complementInsee?.p2EtatMatrimonialCode)
            }
            etatMatrimonialCode={complementInsee?.p2EtatMatrimonialCode}
            dateDivorce={complementInsee?.p2DateDivorce}
            dateVeuvage={complementInsee?.p2DateVeuvage}
            dejaPacse={complementInsee?.p2DejaPacse}
            nombreEnfants={complementInsee?.nombreEnfants}
            showAllInsee={false}
            inseeObligatoire={!isProjet}
            dataInseeOld={
              // Tant que la déclaration n'a jamais été transférée à l'INSEE
              // (dateInitialeInsee non renseignée), les données saisies ne
              // sont pas "épurées" : elles doivent s'afficher normalement.
              !pacs?.dateInitialeInsee ||
              dayjs().diff(dayjs(pacs.dateInitialeInsee), 'month') <= 6
            }
            dateInitialeInsee={pacs?.dateInitialeInsee}
            showDiff={showDiff}
            numeroPartenaire={2}
            idConvention={conventionsDeclarationsId}
            partenaireKey="partenaire2"
            conventionDeclaration={conventionDeclaration}
            complementInsee={complementInsee}
            identifiantPacs={pacs?.identifiant}
            onSaved={fetchConvention}
            onComplementInseeSaved={setComplementInsee}
            updated={conventionDeclarationUpdated}
            onChange={handleChangePartenaire2}
            onSave={handleSaveConventionDeclaration}
            onRollback={handleRollbackConventionDeclaration}
          />
        </Accordion.Content>
      </Accordion>
    ),
    [
      conventionDeclaration,
      conventionDeclaration?.partenaire2,
      conventionDeclaration?.perePartenaire2,
      conventionDeclaration?.merePartenaire2,
      conventionDeclaration?.previousConvention?.partenaire2,
      conventionDeclaration?.previousConvention?.perePartenaire2,
      conventionDeclaration?.previousConvention?.merePartenaire2,
      complementInsee,
      complementInsee?.p2EtatMatrimonialCode,
      complementInsee?.p2EtatMatrimonialLibelle,
      complementInsee?.p2DateDivorce,
      complementInsee?.p2DateVeuvage,
      complementInsee?.p2DejaPacse,
      complementInsee?.nombreEnfants,
      pacs?.dateInitialeInsee,
      pacs?.identifiant,
      isProjet,
      activeIndex,
      handleToggle,
      showDiff,
      conventionDeclaration?.changes?.infoPartenaire2,
      conventionsDeclarationsId,
      fetchConvention,
      conventionDeclarationUpdated,
      handleChangePartenaire2,
      handleSaveConventionDeclaration,
      handleRollbackConventionDeclaration,
    ],
  );

  const informationsDeclarationInitiale = React.useMemo(() => {
    let title = '';
    switch (conventionDeclaration?.action) {
      case 'Conclusion':
        title = 'INFORMATIONS DE LA DÉCLARATION INITIALE';
        break;
      case 'Modification':
        title = 'INFORMATIONS DE LA DÉCLARATION MODIFICATIVE';
        break;
      default:
        title = 'INFORMATIONS DE LA DÉCLARATION';
    }

    return (
      <Accordion fluid styled secondary className="fiche-panel sous-panel">
        <Accordion.Title
          active={activeIndex === 3}
          index={3}
          onClick={() => handleToggle(3)}
        >
          <Icon name={activeIndex === 3 ? 'caret down' : 'caret right'} />
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <span>{title}</span>
            {showDiff &&
              conventionDeclaration?.changes?.infoDeclaration >= 1 && (
                <span>
                  ({conventionDeclaration?.changes?.infoDeclaration}{' '}
                  modification
                  {conventionDeclaration?.changes?.infoDeclaration > 1
                    ? 's'
                    : ''}
                  )
                </span>
              )}
          </div>
        </Accordion.Title>
        <Accordion.Content active={activeIndex === 3}>
          <InformationsDeclarationInitiale
            conventionDeclaration={conventionDeclaration}
            showDiff={showDiff}
            onSaved={fetchConvention}
          />
        </Accordion.Content>
      </Accordion>
    );
  }, [
    activeIndex,
    conventionDeclaration,
    handleToggle,
    showDiff,
    fetchConvention,
  ]);

  const informationsConventionInitiale = React.useMemo(() => {
    let title = '';
    switch (conventionDeclaration?.action) {
      case 'Conclusion':
        title = 'INFORMATIONS DE LA CONVENTION INITIALE';
        break;
      case 'Modification':
        title = 'INFORMATIONS DE LA CONVENTION MODIFICATIVE';
        break;
      default:
        title = 'INFORMATIONS DE LA CONVENTION';
    }

    return (
      <Accordion fluid styled secondary className="fiche-panel sous-panel">
        <Accordion.Title
          active={activeIndex === 4}
          index={4}
          onClick={() => handleToggle(4)}
        >
          <Icon name={activeIndex === 4 ? 'caret down' : 'caret right'} />
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <span>{title}</span>
            {showDiff &&
              conventionDeclaration?.changes?.infoConvention >= 1 && (
                <span>
                  ({conventionDeclaration?.changes?.infoConvention} modification
                  {conventionDeclaration?.changes?.infoConvention > 1
                    ? 's'
                    : ''}
                  )
                </span>
              )}
          </div>
        </Accordion.Title>
        <Accordion.Content active={activeIndex === 4}>
          <InformationsConventionInitiale
            conventionDeclaration={conventionDeclaration}
            showDiff={showDiff}
            onSaved={fetchConvention}
          />
        </Accordion.Content>
      </Accordion>
    );
  }, [
    activeIndex,
    conventionDeclaration,
    handleToggle,
    showDiff,
    fetchConvention,
  ]);

  const informationsDissolution = React.useMemo(
    () => (
      <Accordion fluid styled secondary className="fiche-panel sous-panel">
        <Accordion.Title
          active={activeIndex === 5}
          index={5}
          onClick={() => handleToggle(5)}
        >
          <Icon name={activeIndex === 5 ? 'caret down' : 'caret right'} />
          INFORMATIONS DE DISSOLUTION
        </Accordion.Title>
        <Accordion.Content active={activeIndex === 5}>
          <InformationsDissolution
            conventionDeclaration={conventionDeclaration}
            onSaved={fetchConvention}
          />
        </Accordion.Content>
      </Accordion>
    ),
    [activeIndex, conventionDeclaration, handleToggle, fetchConvention],
  );

  return (
    <div className="informations-conventions-container">
      {loading && (
        <Dimmer active inverted>
          <Loader size="large" />
        </Dimmer>
      )}
      <Accordion>
        {informationsEnregistrementDeclaration}
        {/* Panel présent uniquement lors de la conclusion et des modifications */}
        {conventionDeclaration?.action !== 'Dissolution' &&
          informationsDeclarationInitiale}
        {/* Panel présent uniquement lors de la conclusion et des modifications */}
        {conventionDeclaration?.action !== 'Dissolution' &&
          informationsConventionInitiale}
        {/* Panel présent uniquement lors de la dissolution */}
        {conventionDeclaration?.action === 'Dissolution' &&
          informationsDissolution}
        {informationsPartenaire1}
        {informationsPartenaire2}
      </Accordion>
    </div>
  );
};

export default InformationsConventionsContainer;
